'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { timeAgo } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';
import type { LiveNewsItem } from '@/lib/api-client';

/** A compact "N" badge shown next to a ticker that has recent News Room (live)
 *  headlines. Click opens a popover listing them (each links out).
 *
 *  Two deliberate structural choices let this work in EVERY context:
 *   - the trigger is a <span role="button"> (not a <button>), so it is valid
 *     nested inside an <a> — the watchlist tiles are links;
 *   - the popover renders in a PORTAL with fixed positioning, so an
 *     overflow-hidden ancestor (the ticker popup) can't clip it.
 *
 *  Renders nothing when there's no news. Click/keydown stop propagation +
 *  preventDefault so they don't trigger the surrounding row / ticker link. */
export default function LiveNewsBadge({ items }: { items: LiveNewsItem[] }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const tz = useTimezone();

  const POP_W = 288; // w-72

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Clamp width to the viewport (an inline width would otherwise overflow on
    // phones); center under the badge, then clamp into the viewport.
    const width = Math.min(POP_W, vw - 16);
    const center = r.left + r.width / 2;
    const left = Math.max(8, Math.min(vw - width - 8, center - width / 2));
    const estH = 280;
    // Open below the badge; flip above when there isn't room.
    const top = r.bottom + estH > vh - 8 ? Math.max(8, r.top - estH - 4) : r.bottom + 4;
    setCoords({ top, left, width });
  }, []);

  // Position on open; keep it pinned to the trigger while scrolling/resizing.
  useEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  // Close on outside click (trigger AND popover are excluded) + Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!items || items.length === 0) return null;

  const toggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((o) => !o);
  };

  return (
    <>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); }}
        title={`${items.length} recent headline${items.length === 1 ? '' : 's'} — News Room`}
        aria-label={`${items.length} recent news headlines`}
        className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded px-1 text-[10px] font-bold leading-none bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors cursor-pointer flex-shrink-0 select-none"
      >
        N
      </span>
      {open && coords && typeof document !== 'undefined' && createPortal(
        <div
          ref={popRef}
          // z-index above app modals (watchlist fullscreen z-[9999], history
          // modal z-[200]) so the popover is never covered when opened from
          // inside one.
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, zIndex: 10000 }}
          className="rounded-lg border border-default bg-card-bg p-1 text-left shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {items.slice(0, 8).map((h, i) => (
            <a
              key={`${h.id}-${i}`}
              href={h.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="block rounded border-b border-default/30 px-2 py-1.5 transition-colors last:border-b-0 hover:bg-card-hover-bg"
            >
              <p className="line-clamp-2 text-xs leading-snug text-slate-200">{h.headline}</p>
              <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-fg-subtle">
                <span>{h.source}</span>
                {h.createdAt && <><span>·</span><span>{timeAgo(h.createdAt, tz)}</span></>}
              </div>
            </a>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
